import {
    BAGGAGE_PRICE,
    BASE_PRICE,
    CONV_FEE,
    COUPON_DISCOUNT,
    COUPON_DISCOUNT_WITH_VAT,
    FARE_PRICE,
    GV_DISCOUNT,
    INSURANCE_PRICE,
    INSURANCE_PRICE_WITH_VAT,
    MEAL_PRICE,
    SEAT_PRICE,
    SUPPLIER_DISCOUNT,
    TAXES
} from '@/src/constants/priceSummary'
import type { PaxPricingInfo } from '@/src/types/itinerary'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

export interface PricingSubItem {
    label: string
    value: number
}

export interface PricingItem {
    id: string
    label: string
    value: number
    isDiscount: boolean
    isCollapsible: boolean
    subItems?: PricingSubItem[]
    usePrecision?: boolean
}

interface UsePricingItemsParams {
    farePrice: number
    basePrice: number
    taxes: number
    convenienceFee: number
    fareInsuranceTitle: string
    fareInsuranceTitleWithVat: string
    totalPremiumAmount: number
    totalPremiumAmountWithVat: number
    totalInsurancePriceWithVat: number
    totalInsurancePrice: number
    seatPrice: number
    mealPrice: number
    baggagePrice: number
    totalCouponAmount: number
    totalCouponAmountWithVat: number
    totalInstantDiscount: number
    couponCode: string
    insurancePricingConfig: Record<string, { price: number; isDiscount: boolean }>
    fareFamilyEnabledOnReview: boolean
    paxPricingInfoJson: PaxPricingInfo[]
    passengers: any[]
    faresVat: number
    additionalVat: number
}

/**
 * Hook for creating formatted pricing items for display
 * Handles both fare family and regular fare display modes
 */
const usePricingItems = (params: UsePricingItemsParams) => {
    const t = useTranslations('itineraryPage.pricing')

    const {
        farePrice,
        basePrice,
        taxes,
        convenienceFee,
        fareInsuranceTitle,
        fareInsuranceTitleWithVat,
        totalPremiumAmount,
        totalPremiumAmountWithVat,
        totalInsurancePrice,
        totalInsurancePriceWithVat,
        seatPrice,
        mealPrice,
        baggagePrice,
        totalCouponAmount,
        totalCouponAmountWithVat,
        totalInstantDiscount,
        couponCode,
        insurancePricingConfig,
        fareFamilyEnabledOnReview,
        paxPricingInfoJson,
        passengers,
        faresVat,
        additionalVat
    } = params

    const pricingConfig: Record<string, { price: number; isDiscount: boolean }> = useMemo(
        () => ({
            [BASE_PRICE]: { price: basePrice, isDiscount: false },
            [TAXES]: { price: taxes, isDiscount: false },
            [CONV_FEE]: { price: convenienceFee, isDiscount: false },
            [FARE_PRICE]: { price: farePrice, isDiscount: false },
            [fareInsuranceTitle]: { price: totalPremiumAmount, isDiscount: false },
            [fareInsuranceTitleWithVat]: { price: totalPremiumAmountWithVat, isDiscount: false },
            [INSURANCE_PRICE]: { price: totalInsurancePrice, isDiscount: false },
            [INSURANCE_PRICE_WITH_VAT]: { price: totalInsurancePriceWithVat, isDiscount: false },
            [SEAT_PRICE]: { price: seatPrice, isDiscount: false },
            [MEAL_PRICE]: { price: mealPrice, isDiscount: false },
            [BAGGAGE_PRICE]: { price: baggagePrice, isDiscount: false },
            [COUPON_DISCOUNT]: { price: totalCouponAmount, isDiscount: true },
            [COUPON_DISCOUNT_WITH_VAT]: { price: totalCouponAmountWithVat, isDiscount: true },
            ...insurancePricingConfig
        }),
        [
            basePrice,
            taxes,
            convenienceFee,
            farePrice,
            fareInsuranceTitle,
            totalPremiumAmount,
            totalPremiumAmountWithVat,
            totalInsurancePrice,
            totalInsurancePriceWithVat,
            seatPrice,
            mealPrice,
            baggagePrice,
            totalCouponAmount,
            totalCouponAmountWithVat,
            insurancePricingConfig
        ]
    )

    const getEffectivePricing = (pricingElements: string[]) => {
        let effectivePrice = 0
        if (Array.isArray(pricingElements)) {
            pricingElements.forEach(pricingElement => {
                const { price, isDiscount } = pricingConfig[pricingElement] || {}
                if (!isEmpty(price)) {
                    if (isDiscount) {
                        effectivePrice -= price
                    } else {
                        effectivePrice += price
                    }
                }
            })
        }
        return effectivePrice
    }

    const pricingItems: PricingItem[] = useMemo(() => {
        let basePricingItems: PricingItem[] = [
            {
                id: 'insurance',
                label: fareInsuranceTitle,
                value: pricingConfig[fareInsuranceTitle]?.price || 0,
                isDiscount: false,
                isCollapsible: false
            },
            {
                id: 'convenience-fee',
                label: t('convenienceFee'),
                value: pricingConfig[CONV_FEE]?.price || 0,
                isDiscount: false,
                isCollapsible: false
            },
            {
                id: 'add-ons',
                label: t('addOns'),
                value: getEffectivePricing([SEAT_PRICE, MEAL_PRICE, BAGGAGE_PRICE, INSURANCE_PRICE]),
                isDiscount: false,
                isCollapsible: true,
                subItems: [
                    {
                        label: t('seatSelection'),
                        value: pricingConfig[SEAT_PRICE]?.price || 0
                    },
                    {
                        label: t('mealSelection'),
                        value: pricingConfig[MEAL_PRICE]?.price || 0
                    },
                    {
                        label: t('baggageSelection'),
                        value: pricingConfig[BAGGAGE_PRICE]?.price || 0
                    },
                    ...Object.keys(insurancePricingConfig).map(key => ({
                        label: key,
                        value: insurancePricingConfig[key]?.price || 0
                    }))
                ].filter(item => item.value > 0)
            },
            {
                id: 'gv-discount',
                label: 'Gift voucher discount',
                value: pricingConfig[GV_DISCOUNT]?.price || 0,
                isDiscount: true,
                isCollapsible: false
            },
            {
                id: 'coupon-discount',
                label: t('discounts'),
                value: (pricingConfig[COUPON_DISCOUNT]?.price || 0) + Math.abs(totalInstantDiscount),
                isDiscount: true,
                isCollapsible: true,
                subItems: [
                    {
                        label: `Coupon Code (${couponCode})`,
                        value: Math.abs(totalCouponAmount)
                    },
                    { label: t('instantDiscount'), value: Math.abs(totalInstantDiscount) }
                ].filter(item => item.value > 0)
            },
            {
                id: 'supplier-discount',
                label: 'Supplier discount',
                value: pricingConfig[SUPPLIER_DISCOUNT]?.price || 0,
                isDiscount: true,
                isCollapsible: false
            }
        ].filter(item => item.value > 0)

        if (!fareFamilyEnabledOnReview && Array.isArray(paxPricingInfoJson) && paxPricingInfoJson.length > 0) {
            let totalVat = 0
            const paxPricingItems: PricingItem[] = paxPricingInfoJson.map((pax: PaxPricingInfo, idx: number) => {
                const pricingElements = (pax.pricingInfoJson || []).flatMap(info => info?.pricingElementJson || [])
                const baseFare = pricingElements
                    .filter(e => e.category === 'BF')
                    .reduce((sum: number, e) => sum + (e.amount || 0), 0)
                const taxes = pricingElements
                    .filter(e => ['TAX', 'MKP'].includes(e.category) && !['AIRLINE-VAT', 'CT-VAT'].includes(e.code))
                    .reduce((sum: number, e) => sum + (e.amount || 0), 0)
                const VAT = pricingElements
                    .filter(e => ['AIRLINE-VAT', 'CT-VAT'].includes(e.code))
                    .reduce((sum: number, e) => sum + (e.amount || 0), 0)
                totalVat += VAT
                let type = 'Adult'
                if (passengers[idx]?.type === 'CHD') type = 'Child'
                if (passengers[idx]?.type === 'INF') type = 'Infant'
                return {
                    id: `Traveller-${idx + 1}-fare`,
                    label: t('travelerKey', { travelerNumber: idx + 1, travelerType: t(type) }),
                    value: baseFare + taxes,
                    isDiscount: false,
                    isCollapsible: true,
                    subItems: [
                        { label: t('baseFare'), value: baseFare },
                        { label: t('tax'), value: taxes }
                    ],
                    usePrecision: false
                }
            })
            // Calculate adjusted VAT for per-pax pricing
            const adjustedTotalVat = totalVat + additionalVat
            const vatItem = {
                id: 'vat-detail',
                label: t('vat'),
                value: adjustedTotalVat,
                isDiscount: false,
                isCollapsible: false,
                usePrecision: false
            }
            return [...paxPricingItems, ...basePricingItems, ...(adjustedTotalVat > 0 ? [vatItem] : [])]
        } else {
            return [
                {
                    id: 'base-price',
                    label: t('baseFare'),
                    value: pricingConfig[BASE_PRICE].price,
                    isDiscount: pricingConfig[BASE_PRICE].isDiscount,
                    isCollapsible: false,
                    usePrecision: false
                },
                {
                    id: 'taxes',
                    label: t('tax'),
                    value: pricingConfig[TAXES].price - faresVat,
                    isDiscount: pricingConfig[TAXES].isDiscount,
                    isCollapsible: false,
                    usePrecision: false
                },
                ...basePricingItems,
                {
                    id: 'vat-detail',
                    label: t('vat'),
                    value: faresVat + additionalVat,
                    isDiscount: false,
                    isCollapsible: false,
                    usePrecision: false
                }
            ].filter(item => item.value > 0)
        }
    }, [
        pricingConfig,
        getEffectivePricing,
        fareFamilyEnabledOnReview,
        paxPricingInfoJson,
        passengers,
        fareInsuranceTitle,
        totalCouponAmount,
        totalInstantDiscount,
        couponCode,
        insurancePricingConfig,
        faresVat,
        additionalVat,
        t
    ])

    return {
        pricingItems,
        pricingConfig,
        getEffectivePricing
    }
}

export default usePricingItems
